import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Empty,
  Input,
  InputNumber,
  List,
  Popconfirm,
  Select,
  Space,
  Tag,
  Typography,
  Upload,
  message
} from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import { DeleteOutlined, DownloadOutlined, ReloadOutlined, UploadOutlined } from '@ant-design/icons';
import {
  deleteLeadOffer,
  downloadLeadOfferPdf,
  fetchLeadOffers,
  reanalyzeLeadOffer,
  updateLeadOffer,
  uploadLeadOffer
} from './api';
import type { LeadOffer } from './needsAnalysisTypes';

const { Text, Title } = Typography;

type Props = {
  leadId: string;
  canManage: boolean;
};

function formatAmount(value: number | null, currency: string): string {
  if (value === null || !Number.isFinite(value)) {
    return '—';
  }
  return `${value.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
}

function parseList(value: string): string[] {
  return value.split('\n').map((line) => line.trim()).filter(Boolean);
}

export default function CustomerOfferPanel({ leadId, canManage }: Props) {
  const [offers, setOffers] = useState<LeadOffer[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<LeadOffer | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchLeadOffers(leadId);
      setOffers(response.offers);
      setSelectedId((current) => current || response.offers[0]?.id || null);
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke hente tilbud');
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    void load();
  }, [load]);

  const selected = useMemo(
    () => offers.find((offer) => offer.id === selectedId) || null,
    [offers, selectedId]
  );

  useEffect(() => {
    setDraft(selected ? { ...selected } : null);
  }, [selected]);

  const runAction = useCallback(async (action: () => Promise<void>) => {
    setBusy(true);
    try {
      await action();
    } catch (error: any) {
      message.error(error?.message || 'Handlingen kunne ikke gennemføres');
    } finally {
      setBusy(false);
    }
  }, []);

  const previewTotal = useMemo(() => {
    if (!draft || draft.base_premium === null) {
      return null;
    }
    const markup = Math.round(draft.base_premium * ((draft.markup_percent ?? 0) / 100) * 100) / 100;
    return Math.round((draft.base_premium + markup) * 100) / 100;
  }, [draft]);

  const upload = useCallback(() => runAction(async () => {
    const file = fileList[0]?.originFileObj;
    if (!file) {
      message.error('Vælg en fil');
      return;
    }
    const offer = await uploadLeadOffer(leadId, file as File);
    setFileList([]);
    await load();
    setSelectedId(offer.id);
    message.success('Tilbuddet er uploadet og analyseret');
  }), [fileList, leadId, load, runAction]);

  const save = useCallback(() => runAction(async () => {
    if (!draft) return;
    await updateLeadOffer(leadId, draft.id, {
      insurer: draft.insurer,
      base_premium: draft.base_premium,
      markup_percent: draft.markup_percent,
      currency: draft.currency,
      advantages: draft.advantages,
      improvements: draft.improvements,
      broker_note: draft.broker_note
    });
    await load();
    message.success('Tilbuddet er gemt');
  }), [draft, leadId, load, runAction]);

  const downloadPdf = useCallback(() => runAction(async () => {
    if (!selected) return;
    const blob = await downloadLeadOfferPdf(leadId, selected.id);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'tilbud.pdf';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }), [leadId, runAction, selected]);

  return (
    <Card title="Tilbud til kunden" loading={loading}>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        {canManage ? (
          <Space size={8} wrap>
            <Upload
              accept=".pdf,.doc,.docx,.xlsx,.png,.jpg,.jpeg"
              maxCount={1}
              fileList={fileList}
              beforeUpload={() => false}
              onChange={({ fileList: next }) => setFileList(next.slice(-1))}
            >
              <Button icon={<UploadOutlined />}>Vælg selskabstilbud</Button>
            </Upload>
            <Button type="primary" loading={busy} disabled={!fileList.length} onClick={upload}>
              Upload og analysér
            </Button>
          </Space>
        ) : null}

        {!offers.length ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Der er endnu ingen tilbud på dette lead" />
        ) : (
          <>
            <Space size={8} wrap>
              {offers.map((offer) => (
                <Tag
                  key={offer.id}
                  color={offer.id === selectedId ? 'processing' : 'default'}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelectedId(offer.id)}
                >
                  {offer.insurer || offer.file_name}
                </Tag>
              ))}
            </Space>

            {draft ? (
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                {draft.analysis_status === 'FAILED' ? (
                  <Alert type="error" showIcon message={draft.analysis_error || 'AI-analysen fejlede'} />
                ) : null}

                <Space size={8} wrap>
                  <Tag color={draft.analysis_status === 'COMPLETED' ? 'success' : 'warning'}>
                    {draft.analysis_status === 'COMPLETED' ? 'Analyseret' : draft.analysis_status}
                  </Tag>
                  <Button
                    size="small"
                    icon={<ReloadOutlined />}
                    loading={busy}
                    onClick={() => runAction(async () => {
                      await reanalyzeLeadOffer(leadId, draft.id);
                      await load();
                      message.success('Tilbuddet er analyseret igen');
                    })}
                  >
                    Ny AI-analyse
                  </Button>
                  <Button icon={<DownloadOutlined />} loading={busy} onClick={downloadPdf}>
                    Hent tilbud som PDF
                  </Button>
                  {canManage ? (
                    <Popconfirm
                      title="Slet tilbuddet?"
                      okText="Slet"
                      cancelText="Annuller"
                      onConfirm={() => runAction(async () => {
                        await deleteLeadOffer(leadId, draft.id);
                        setSelectedId(null);
                        await load();
                        message.success('Tilbuddet er slettet');
                      })}
                    >
                      <Button danger icon={<DeleteOutlined />}>Slet</Button>
                    </Popconfirm>
                  ) : null}
                </Space>

                {draft.analysis_summary ? <Text>{draft.analysis_summary}</Text> : null}

                <Card size="small" title="Pris til kunden">
                  <Space direction="vertical" size={10} style={{ width: '100%' }}>
                    <Space size={10} wrap>
                      <Text style={{ width: 220, display: 'inline-block' }}>Forsikringsselskab</Text>
                      <Input
                        disabled={!canManage}
                        style={{ width: 280 }}
                        value={draft.insurer || ''}
                        onChange={(event) => setDraft({ ...draft, insurer: event.target.value })}
                      />
                    </Space>
                    <Space size={10} wrap>
                      <Text style={{ width: 220, display: 'inline-block' }}>Præmie fra selskabet</Text>
                      <InputNumber
                        disabled={!canManage}
                        style={{ width: 200 }}
                        value={draft.base_premium ?? undefined}
                        decimalSeparator=","
                        onChange={(value) => setDraft({ ...draft, base_premium: value === null || value === undefined ? null : Number(value) })}
                      />
                      <Select
                        disabled={!canManage}
                        style={{ width: 100 }}
                        value={draft.currency}
                        onChange={(value) => setDraft({ ...draft, currency: value })}
                        options={['DKK', 'EUR', 'USD'].map((value) => ({ value, label: value }))}
                      />
                    </Space>
                    <Space size={10} wrap>
                      <Text style={{ width: 220, display: 'inline-block' }}>Mæglerhonorar (%)</Text>
                      <InputNumber
                        disabled={!canManage}
                        style={{ width: 200 }}
                        min={0}
                        max={100}
                        value={draft.markup_percent ?? undefined}
                        onChange={(value) => setDraft({ ...draft, markup_percent: value === null || value === undefined ? null : Number(value) })}
                      />
                    </Space>
                    <Space size={10} wrap>
                      <Text style={{ width: 220, display: 'inline-block' }} strong>Samlet pris til kunden</Text>
                      <Text strong>{formatAmount(previewTotal ?? draft.total_price, draft.currency)}</Text>
                    </Space>
                  </Space>
                </Card>

                <Card size="small" title="Fordele ved løsningen">
                  <Input.TextArea
                    disabled={!canManage}
                    autoSize={{ minRows: 3, maxRows: 8 }}
                    placeholder="Én fordel pr. linje"
                    value={draft.advantages.join('\n')}
                    onChange={(event) => setDraft({ ...draft, advantages: parseList(event.target.value) })}
                  />
                </Card>

                <Card size="small" title="Forbedringer i forhold til nuværende forsikring">
                  <Input.TextArea
                    disabled={!canManage}
                    autoSize={{ minRows: 3, maxRows: 8 }}
                    placeholder="Én forbedring pr. linje"
                    value={draft.improvements.join('\n')}
                    onChange={(event) => setDraft({ ...draft, improvements: parseList(event.target.value) })}
                  />
                </Card>

                <Card size="small" title="Rådgivers bemærkninger">
                  <Input.TextArea
                    disabled={!canManage}
                    autoSize={{ minRows: 2, maxRows: 6 }}
                    value={draft.broker_note || ''}
                    onChange={(event) => setDraft({ ...draft, broker_note: event.target.value })}
                  />
                </Card>

                {canManage ? (
                  <Button type="primary" loading={busy} onClick={save}>
                    Gem tilbud
                  </Button>
                ) : null}

                {Array.isArray((draft.analysis_payload as any)?.coverages) ? (
                  <Card size="small" title="Dækninger fra selskabets tilbud">
                    <List
                      size="small"
                      dataSource={(draft.analysis_payload as any).coverages as Array<Record<string, unknown>>}
                      renderItem={(coverage, index) => (
                        <List.Item key={index}>
                          <Space direction="vertical" size={2}>
                            <Title level={5} style={{ margin: 0 }}>{String(coverage.name ?? 'Ukendt dækning')}</Title>
                            <Text type="secondary">
                              Sum: {String(coverage.limit ?? '—')} · Selvrisiko: {String(coverage.deductible ?? '—')}
                            </Text>
                          </Space>
                        </List.Item>
                      )}
                    />
                  </Card>
                ) : null}
              </Space>
            ) : null}
          </>
        )}
      </Space>
    </Card>
  );
}
